import React, {useState, useEffect, useCallback} from 'react';
import axios from 'axios';
import styles from './UserForm.module.css';  // Import CSS module
import {getSiteTypeFromHostname, getHostIP} from '../../networking/Hosts';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {readStoredHpvLocation, requestHpvLocation} from '../../utils/hpvLocation';
import {RSV_SERVICE_REGIONS, RSV_HOSPITALS} from '../../data/rsvRegions';
import {FLU_REGIONS} from '../../data/fluRegions';
import HpvSubsidyModal from '../../components/HpvSubsidyModal';
import {
    getRemainingHpvConversations,
    HPV_TARGET_CONVERSATIONS
} from '../../data/hpvSubsidy';

const getModelNameForSiteType = (siteType) => {
    if (siteType === 'cancer') {
        return 'CRC';
    }
    if (siteType === 'hpv') {
        return 'HPV';
    }
    if (siteType === 'rsv') {
        return 'RSV';
    }
    return '流感';
};

const getPageTitleForSiteType = (siteType) => {
    if (siteType === 'cancer') {
        return '肠癌筛查智能问答助手';
    }
    if (siteType === 'hpv') {
        return 'HPV疫苗专属智能助手';
    }
    if (siteType === 'rsv') {
        return 'RSV预防智能问答助手';
    }
    return '流感疫苗智能问答助手';
};

const HPV_EXIT_REMINDER_KEY = 'hpvExitReminderEligible';
const HPV_APPOINTMENT_REGION_KEY = 'hpvAppointmentRegion';
const HPV_BIRTH_MIN_YEAR = 1980;
const HPV_BIRTH_MAX_YEAR = 2012;
const hpvAppointmentLinks = {
    安徽: {
        region: '池州',
        label: '池州预约入口',
        url: '#小程序://皖事通/4CjXaA9HZAtNGty',
        weappUsername: process.env.REACT_APP_HPV_CHIZHOU_WEAPP_USERNAME || '',
        weappPath: process.env.REACT_APP_HPV_CHIZHOU_WEAPP_PATH || ''
    },
    甘肃: {
        region: '兰州',
        label: '兰州预约入口',
        url: '#小程序://甘肃疾控/FfxweOyC2r7Merh',
        weappUsername: process.env.REACT_APP_HPV_LANZHOU_WEAPP_USERNAME || '',
        weappPath: process.env.REACT_APP_HPV_LANZHOU_WEAPP_PATH || ''
    },
    浙江: {
        region: '浙江',
        label: '浙江预约入口',
        url: '#小程序://浙里办/CiVhuN0O8denNTf',
        weappUsername: process.env.REACT_APP_HPV_ZHEJIANG_WEAPP_USERNAME || '',
        weappPath: process.env.REACT_APP_HPV_ZHEJIANG_WEAPP_PATH || ''
    }
};

const rangeNumbers = (start, end) => (
    Array.from({length: end - start + 1}, (_, index) => start + index)
);

const padDatePart = (value) => String(value).padStart(2, '0');

const getHpvAppointmentInfo = (location) => {
    const normalizedLocation = String(location || '');

    if (
        normalizedLocation.includes('安徽') ||
        normalizedLocation.includes('池州') ||
        normalizedLocation.includes('贵池') ||
        normalizedLocation.includes('青阳') ||
        normalizedLocation.includes('东至')
    ) {
        return hpvAppointmentLinks.安徽;
    }

    if (
        normalizedLocation.includes('甘肃') ||
        normalizedLocation.includes('兰州') ||
        normalizedLocation.includes('城关') ||
        normalizedLocation.includes('榆中')
    ) {
        return hpvAppointmentLinks.甘肃;
    }

    if (normalizedLocation.includes('浙江')) {
        return hpvAppointmentLinks.浙江;
    }

    return null;
};

const getHpvMonthOptions = (year) => {
    if (!year) return [];
    return Number(year) === HPV_BIRTH_MAX_YEAR ? [1] : rangeNumbers(1, 12);
};

const getHpvDayOptions = (year, month) => {
    if (!year || !month) return [];

    if (Number(year) === HPV_BIRTH_MAX_YEAR && Number(month) === 1) {
        return [1];
    }

    const daysInMonth = new Date(Number(year), Number(month), 0).getDate();
    return rangeNumbers(1, daysInMonth);
};

const storeHpvAppointmentRegion = (location) => {
    const appointmentInfo = getHpvAppointmentInfo(location);

    if (appointmentInfo) {
        sessionStorage.setItem(HPV_APPOINTMENT_REGION_KEY, appointmentInfo.region);
    }
};

const ClinicReminderPanel = ({data}) => {
    if (!data) return null;
    const clinics = Array.isArray(data.clinics) ? data.clinics : [];

    if (data.status !== 'ok') {
        return (
            <div className={styles.clinicPanel}>
                <p className={styles.clinicMessage}>{data.message || '暂时无法查询附近接种点。'}</p>
                {data.accuracy_warning && (
                    <p className={styles.clinicMessage}>{data.accuracy_warning}</p>
                )}
            </div>
        );
    }

    const nearest = clinics[0];
    if (!nearest) return null;

    return (
        <div className={styles.clinicPanel}>
            <p className={styles.clinicMessage}>离您最近的 HPV 接种点：</p>
            {data.accuracy_warning && (
                <p className={styles.clinicMessage}>{data.accuracy_warning}</p>
            )}
            <div className={styles.clinicName}>{nearest.name}</div>
            <div className={styles.clinicMeta}>约 {nearest.distance_km} km · {nearest.address}</div>
            {nearest.phone && <div className={styles.clinicMeta}>电话：{nearest.phone}</div>}
            <div className={styles.clinicActions}>
                <a href={nearest.map_url} target="_blank" rel="noopener noreferrer">查看地图</a>
                <a href={nearest.navigation_url} target="_blank" rel="noopener noreferrer">导航前往</a>
            </div>
        </div>
    );
};

const UserForm = () => {
    const [searchParams] = useSearchParams();
    const siteType = getSiteTypeFromHostname();
    const isRsvSite = siteType === 'rsv';
    const isFluStyleSite = siteType === 'vax' || siteType === 'rsv';
    const currentHostIP = getHostIP();
    const localApiHost = searchParams.get('apiHost');
    const appendLocalApiHost = (queryParams) => {
        if (localApiHost) {
            queryParams.set('apiHost', localApiHost);
        }
        return queryParams;
    };
    
    // 检测是否是移动设备（默认是手机版）
    const [isMobile, setIsMobile] = useState(true);

    const handleResize = () => {
        // 检查userAgent是否为移动设备
        const ua = navigator.userAgent.toLowerCase();
        const isMobileUA = /android|webos|iphone|ipad|ipod|ios|blackberry|iemobile|opera mini|micromessenger|wechat/i.test(ua);

        // 检查屏幕宽度（更宽松的判断）
        const isMobileWidth = window.innerWidth <= 1024;

        console.log(isMobileUA, isMobileWidth);

        // 如果是移动设备userAgent或屏幕宽度小于等于1024px，则认为是移动端
        setIsMobile(isMobileUA || isMobileWidth);
    };
    // 监听窗口大小变化
    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [searchParams]);
    
    
    // 省市、区县、社区医院数据（包含所有数据）
    const allLocations = [
        {
            province: '浙江省',
            city: '杭州市',
            districts: [
                {
                    name: '萧山区',
                    hospitals: [
                        '衙前镇社区卫生服务中心',
                        '党湾镇社区卫生服务中心',
                        '进化镇社区卫生服务中心',
                        '所前镇社区卫生服务中心',
                        '蜀山街道社区卫生服务中心',
                        '城厢街道社区卫生服务中心',
                        '靖江街道社区卫生服务中心'
                    ]
                },
                {
                    name: '建德市',
                    hospitals: [
                        '杨村桥镇卫生院',
                        '大洋镇卫生院',
                        '乾潭镇中心卫生院',
                        '航头镇卫生院'
                    ]
                }
            ]
        },
        {
            province: '山西省',
            city: '太原市',
            districts: [
                {
                    name: '小店区',
                    hospitals: [
                        '黄陵社区卫生服务中心',
                        '北格镇卫生院'
                    ]
                },
                {
                    name: '杏花岭区',
                    hospitals: [
                        '敦化坊中心',
                        '中涧河镇小返卫生院'
                    ]
                },
                {
                    name: '清徐县',
                    hospitals: [
                        '东于镇中心卫生院',
                        '徐沟镇中心卫生院',
                        '集义乡中心卫生院'
                    ]
                }
            ]
        },
        {
            province: '云南省',
            city: '大理州',
            districts: [
                {
                    name: '大理市',
                    hospitals: [
                        '太邑彝族乡卫生院',
                        '银桥镇卫生院',
                        '挖色镇卫生院'
                    ]
                },
                {
                    name: '祥云县',
                    hospitals: [
                        '云南驿中心卫生院',
                        '刘厂卫生院',
                        '鹿鸣卫生院'
                    ]
                },
                {
                    name: '宾川县',
                    hospitals: [
                        '宾居镇卫生院',
                        '力角镇卫生院',
                        '拉乌乡卫生院'
                    ]
                }
            ]
        },
        {
            province: '江苏省',
            city: '启东县',
            districts: [
                {
                    name: '吕四港镇-吕四区域',
                    hospitals: [
                        '吕北村',
                        '菜园村',
                        '太阳庙村',
                        '吕滨村'
                    ]
                },
                {
                    name: '吕四港镇-茅家港区域',
                    hospitals: [
                        '十甲村',
                        '海渔村'
                    ]
                },
                {
                    name: '吕四港镇-秦潭区域',
                    hospitals: [
                        '垦北村',
                        '石堤村'
                    ]
                },
                {
                    name: '吕四港镇-天汾区域',
                    hospitals: [
                        '天汾镇村',
                        '西宁村',
                        '念四总村',
                        '闸河村'
                    ]
                },
                {
                    name: '吕四港镇-兆民区域',
                    hospitals: [
                        '吕复村',
                        '洋章村',
                        '元鹏村',
                        '通兴镇村',
                        '巴西村'
                    ]
                }
            ]
        },
        {
            province: '江苏省',
            city: '崇川区',
            districts: [
                {
                    name: '和平桥街道',
                    hospitals: [
                        '颐和社区居委会',
                        '起凤社区居委会',
                        '濠北社区居委会',
                        '濠城社区居委会'
                    ]
                },
                {
                    name: '钟秀街道',
                    hospitals: [
                        '百花社区居委会',
                        '中心社区居委会',
                        '校北社区居委会',
                        '生建社区居委会'
                    ]
                }
            ]
        },
        {
            province: '江苏省',
            city: '通州区',
            districts: [
                {
                    name: '兴仁镇',
                    hospitals: [
                        '兴仁社区',
                        '李家楼村',
                        '葛长路村',
                        '徐庄村',
                        '长林桥村',
                        '丁涧店村',
                        '阚家庵村',
                        '戚家桥村',
                        '温桥村'
                    ]
                }
            ]
        }
    ];

    // RSV、流感开放四个服务区域。
    const rsvLocations = RSV_SERVICE_REGIONS.map(region => ({
        province: region.province,
        city: region.city,
        label: region.label,
        districts: []
    }));
    const locations = siteType === 'cancer'
        ? allLocations.filter(loc => loc.province === '江苏省')
        : siteType === 'vax' ? FLU_REGIONS.map(region => ({...region, districts: []}))
        : isFluStyleSite
            ? rsvLocations.map(loc => siteType === 'vax' && loc.city === '武汉市' ? {...loc, label: '武汉市'} : loc)
            : [...allLocations.filter(loc => loc.province !== '江苏省'), {province: '浙江省', city: '嘉兴市', districts: []}];

    const hpvLocations = [
        {
            province: '安徽',
            districts: ['贵池区', '青阳县', '东至县']
        },
        {
            province: '甘肃',
            districts: ['城关区', '榆中县']
        },
        {
            province: '浙江',
            districts: ['待定']
        }
    ];

    const [phone, setPhone] = useState('');
    const [genderMale, setGenderMale] = useState(null);
    const [welcomeMessage, setWelcomeMessage] = useState('');
    const [showForm, setShowForm] = useState(true);
    const navigate = useNavigate();
    const [showReminder, setShowReminder] = useState(false);
    const [showHpvSubsidyModal, setShowHpvSubsidyModal] = useState(false);
    const [chatCount, setChatCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false); // 加载状态，防止重复提交
    const [nearestClinicData, setNearestClinicData] = useState(null);
    const [isClinicLoading, setIsClinicLoading] = useState(false);
    const [clinicLocationRequested, setClinicLocationRequested] = useState(false);
    
    // 新增的状态变量
    const [name, setName] = useState('');
    const [idCardLastFour, setIdCardLastFour] = useState('');
    const [fluRelationship, setFluRelationship] = useState('');
    const [errorFluRelationship, setErrorFluRelationship] = useState('');
    const [rsvRelationship, setRsvRelationship] = useState('');
    const [rsvBirthMonth, setRsvBirthMonth] = useState('');
    const [rsvBirthDay, setRsvBirthDay] = useState('');
    const [rsvError, setRsvError] = useState('');
    const currentYear = new Date().getFullYear();
    const rsvBirthDate = rsvBirthMonth && rsvBirthDay
        ? `${currentYear}-${rsvBirthMonth.padStart(2, '0')}-${rsvBirthDay.padStart(2, '0')}` : '';
    const rsvDaysInMonth = rsvBirthMonth ? new Date(currentYear, Number(rsvBirthMonth), 0).getDate() : 31;

    const [selectedProvince, setSelectedProvince] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedDistrict, setSelectedDistrict] = useState('');
    const [selectedHospital, setSelectedHospital] = useState('');
    const isJiaxing = selectedCity === '嘉兴市';
    const fluHospitals = siteType === 'vax' ? (FLU_REGIONS.find(r => r.province === selectedProvince && r.city === selectedCity)?.hospitals || []) : [];
    const hasFluHospitals = fluHospitals.length > 0;
    const isRsvShanghai = isRsvSite && (selectedCity === '上海市' || selectedProvince === '青海省');
    const shanghaiRsvHospitals = RSV_HOSPITALS[selectedCity || selectedProvince] || [];
    const jiaxingHospitals = isRsvSite
        ? ['长水街道社区卫生服务中心', '魏塘街道社区卫生服务中心']
        : ['魏塘街道社区卫生服务中心', '西塘镇卫生院', '凤桥镇中心卫生院', '南湖街道社区卫生服务中心', '东栅街道社区卫生服务中心'];

    const [selectedTown, setSelectedTown] = useState(''); // 乡镇
    const [selectedVillage, setSelectedVillage] = useState(''); // 村
    const [identity, setIdentity] = useState(''); // 身份：筛查本人、子女、医生
    const [hpvUserRole, setHpvUserRole] = useState('');
    const [hpvBirthDate, setHpvBirthDate] = useState('');
    const [hpvBirthYear, setHpvBirthYear] = useState('');
    const [hpvBirthMonth, setHpvBirthMonth] = useState('');
    const [hpvBirthDay, setHpvBirthDay] = useState('');
    const [hpvProvince, setHpvProvince] = useState('');
    const [hpvDistrict, setHpvDistrict] = useState('');
    const [hpvInitialAttitude, setHpvInitialAttitude] = useState('');

    // cancer应用专用字段
    const [patientName, setPatientName] = useState(''); // 筛查者姓名
    const [patientGender, setPatientGender] = useState(''); // 筛查者性别
    const [patientPhoneLastFour, setPatientPhoneLastFour] = useState(''); // 筛查者手机号后4位
    const [relationship, setRelationship] = useState(''); // 关系：本人、配偶、子女、其他
    const [patientProvince, setPatientProvince] = useState(''); // 筛查者所在省市
    const [patientCity, setPatientCity] = useState(''); // 筛查者所在城市
    const [patientProvinceCity, setPatientProvinceCity] = useState(''); // 筛查者省市组合（用于选择器显示）
    const [patientDistrict, setPatientDistrict] = useState(''); // 筛查者所在区县
    const [patientHospital, setPatientHospital] = useState(''); // 筛查者所在医院
    
    // 自动朗读开关，默认开启
    const [autoPlay, setAutoPlay] = useState(() => {
        const saved = localStorage.getItem('autoPlayVoice');
        return saved !== null ? saved === 'true' : true; // 默认开启
    });
    
    // 根据选择的省市获取对应的区县和医院
    const selectedLocation = selectedProvince ?
        locations.find(loc => loc.province === selectedProvince && loc.city === selectedCity) : null;
    const districts = selectedLocation ? selectedLocation.districts : [];
    const hospitals = selectedDistrict && selectedLocation ? 
        selectedLocation.districts.find(dist => dist.name === selectedDistrict)?.hospitals || [] : [];
    const selectedHpvProvince = hpvProvince ? hpvLocations.find(loc => loc.province === hpvProvince) : null;
    const hpvDistricts = selectedHpvProvince ? selectedHpvProvince.districts : [];
    const hpvBirthYears = rangeNumbers(HPV_BIRTH_MIN_YEAR, HPV_BIRTH_MAX_YEAR);
    const hpvBirthMonths = getHpvMonthOptions(hpvBirthYear);
    const hpvBirthDays = getHpvDayOptions(hpvBirthYear, hpvBirthMonth);
    const hpvAppointmentInfo = siteType === 'hpv' ?
        getHpvAppointmentInfo(hpvProvince || sessionStorage.getItem(HPV_APPOINTMENT_REGION_KEY)) :
        null;

    // cancer应用的区域选择逻辑
    const patientSelectedLocation = patientProvince && patientCity ?
        locations.find(loc => loc.province === patientProvince && loc.city === patientCity) : null;
    const patientDistricts = patientSelectedLocation ? patientSelectedLocation.districts : [];
    const patientHospitals = patientDistrict && patientSelectedLocation ?
        patientSelectedLocation.districts.find(dist => dist.name === patientDistrict)?.hospitals || [] : [];

    // Error state variables
    const [errorGender, setErrorGender] = useState('');
    const [errorName, setErrorName] = useState('');
    const [errorIdCard, setErrorIdCard] = useState('');
    const [errorProvince, setErrorProvince] = useState('');
    const [errorDistrict, setErrorDistrict] = useState('');
    const [errorHospital, setErrorHospital] = useState('');
    const [errorTown, setErrorTown] = useState('');
    const [errorVillage, setErrorVillage] = useState('');
    const [errorIdentity, setErrorIdentity] = useState('');
    const [errorHpvUserRole, setErrorHpvUserRole] = useState('');
    const [errorHpvBirthDate, setErrorHpvBirthDate] = useState('');
    const [errorHpvLocation, setErrorHpvLocation] = useState('');
    const [errorHpvInitialAttitude, setErrorHpvInitialAttitude] = useState('');

    // cancer应用专用错误状态
    const [errorPatientName, setErrorPatientName] = useState('');
    const [errorPatientGender, setErrorPatientGender] = useState('');
    const [errorPatientPhone, setErrorPatientPhone] = useState('');
    const [errorRelationship, setErrorRelationship] = useState('');
    const [errorPatientProvince, setErrorPatientProvince] = useState('');
    const [errorPatientDistrict, setErrorPatientDistrict] = useState('');
    const [errorPatientHospital, setErrorPatientHospital] = useState('');

    // 保存自动朗读设置到 localStorage
    useEffect(() => {
        localStorage.setItem('autoPlayVoice', autoPlay.toString());
    }, [autoPlay]);

    useEffect(() => {
        if (siteType !== 'hpv') return;

        const validMonths = getHpvMonthOptions(hpvBirthYear);
        if (hpvBirthMonth && !validMonths.includes(Number(hpvBirthMonth))) {
            setHpvBirthMonth('');
            setHpvBirthDay('');
            setHpvBirthDate('');
            return;
        }

        const validDays = getHpvDayOptions(hpvBirthYear, hpvBirthMonth);
        if (hpvBirthDay && !validDays.includes(Number(hpvBirthDay))) {
            setHpvBirthDay('');
            setHpvBirthDate('');
            return;
        }

        if (!hpvBirthYear || !hpvBirthMonth || !hpvBirthDay) {
            setHpvBirthDate('');
            return;
        }

        setHpvBirthDate(`${hpvBirthYear}-${padDatePart(hpvBirthMonth)}-${padDatePart(hpvBirthDay)}`);
    }, [siteType, hpvBirthYear, hpvBirthMonth, hpvBirthDay]);

    // 欢迎文字和继续对话/新聊天判断，以及设置页面标题
    useEffect(() => {

        axios.get(currentHostIP + '/api/getRecentModel', {withCredentials: true})
            .then(response => {
                // 检查是否有匹配当前应用类型的最近对话
                const expectedModel = getModelNameForSiteType(siteType);
                if (response.data.model && response.data.model === expectedModel) {
                    setWelcomeMessage('您希望继续之前的对话，还是重新开始新的聊天？');
                    setShowForm(false);  // 如果有匹配的最近对话，不显示表单
                } else {
                    setWelcomeMessage('');
                    setShowForm(true);  // 没有匹配的对话记录，显示表单
                }
            })
            .catch(error => {
                console.error('获取模型时出错:', error);
                setWelcomeMessage('');
                setShowForm(true);  // 没有对话记录，显示表单
            });
        
        // 根据siteType类型设置页面标题
        document.title = getPageTitleForSiteType(siteType);
    }, [currentHostIP, siteType]);

    const fetchNearestClinic = useCallback(async (location) => {
        setIsClinicLoading(true);
        try {
            const response = await axios.post(`${currentHostIP}/api/nearestHpvClinic`, {
                latitude: location.latitude,
                longitude: location.longitude,
                accuracy: location.accuracy,
                coordinate_system: location.coordinate_system || 'wgs84',
                limit: 1
            });
            setNearestClinicData(response.data);
        } catch (error) {
            console.error('查询最近接种点失败:', error);
            setNearestClinicData({
                status: 'error',
                message: '查询最近接种点失败，请稍后再试。',
                clinics: []
            });
        } finally {
            setIsClinicLoading(false);
        }
    }, [currentHostIP]);

    useEffect(() => {
        // Flu/RSV exit registration is triggered by returning from chat, not history counts.
        if (isFluStyleSite) return;

        if (siteType === 'hpv') {
            const shouldShowHpvReminder = sessionStorage.getItem(HPV_EXIT_REMINDER_KEY) === 'true';
            sessionStorage.removeItem(HPV_EXIT_REMINDER_KEY);

            if (!shouldShowHpvReminder) {
                return;
            }
        }

        axios.get(currentHostIP + '/api/getConversationCount', {
            params: {
                vaccine_type: getModelNameForSiteType(siteType)
            }
        }).then(response => {
            const completedCount = Math.floor((response.data.count || 0) / 2);
            const isUnderTarget = completedCount < HPV_TARGET_CONVERSATIONS;
            const isRecentSession = response.data.time === undefined || response.data.time < 120;

            if (siteType === 'hpv') {
                setChatCount(completedCount);
                setShowReminder(true);
                return;
            }

            if (!isFluStyleSite && isUnderTarget && isRecentSession) {
                setShowReminder(true);
                setChatCount(completedCount);
            }
        }).catch(error => {
            console.error('获取问答次数时出错:', error);
        });
    }, [currentHostIP, isFluStyleSite, siteType]);

    useEffect(() => {
        if (siteType !== 'hpv' || !showReminder) return;

        setNearestClinicData(null);
        setClinicLocationRequested(false);
        const storedLocation = readStoredHpvLocation();
        if (storedLocation) {
            setClinicLocationRequested(true);
            fetchNearestClinic(storedLocation);
        }
    }, [siteType, showReminder, fetchNearestClinic]);

    const handleContinue = () => {
        if (isLoading) return; // 防止重复点击
        setShowReminder(false);
        handleContinueChat(false);
    };

    const handleViewNearestClinic = async () => {
        if (isClinicLoading) return;
        setClinicLocationRequested(true);
        try {
            const location = readStoredHpvLocation() || await requestHpvLocation();
            await fetchNearestClinic(location);
        } catch (error) {
            console.error('定位授权失败:', error);
            setNearestClinicData({
                status: 'error',
                message: '未能获取您的位置。您可以允许浏览器定位后再试。',
                clinics: []
            });
        }
    };

    const handleLeave = () => {
        setShowReminder(false);

        if (isRsvSite) {
            const queryParams = new URLSearchParams();
            appendLocalApiHost(queryParams);
            const query = queryParams.toString();
            navigate(`/reservation${query ? `?${query}` : ''}`);
            return;
        }

        if (siteType === 'hpv') {
            if (window.WeixinJSBridge) {
                window.WeixinJSBridge.call('closeWindow');
                return;
            }

            window.close();
            setTimeout(() => {
                if (!window.closed) {
                    window.location.href = 'about:blank';
                }
            }, 300);
            return;
        }

        window.location.href = "/reservation"; // 跳转到预约页面
        // 执行退出逻辑
        console.log("用户狠心离开");
    };

    const handleContinueChat = (newCredential) => {
        if (isLoading) return; // 防止重复点击
        setIsLoading(true);
        axios.get(currentHostIP + '/api/getRecentModel', {withCredentials: true})
            .then(async response => {
                // 检查是否有匹配当前应用类型的最近对话
                const expectedModel = getModelNameForSiteType(siteType);
                if (response.data.model && response.data.model === expectedModel) {
                    if (newCredential) {
                        await axios.post(currentHostIP + `/api/requestNewCookie`, {})
                    }
                    // 跳转到继续聊天的页面
                    const queryParams = new URLSearchParams({
                        name: response.data.name || '',
                        gender: response.data.gender || ''
                    });
                    // 如果当前是电脑版，传递mobile=false参数
                    if (isMobile) {
                        queryParams.set('mobile', 'true');
                    } else {
                        queryParams.set('mobile', 'false');
                    }
                    appendLocalApiHost(queryParams);
                    if (siteType === 'hpv') {
                        storeHpvAppointmentRegion(
                            response.data.user_location ||
                            response.data.location ||
                            response.data.province ||
                            response.data.district
                        );
                        sessionStorage.setItem(HPV_EXIT_REMINDER_KEY, 'true');
                    }
                    navigate("/chat?" + queryParams.toString());
                } else {
                    setIsLoading(false);
                }
            })
            .catch(error => {
                console.error('继续对话时出错:', error);
                setIsLoading(false); // 出错时重置loading状态
            });
    };

    // 处理前往视频连线（已有 credential 的情况）
    const handleGoToVoiceCall = async () => {
        if (isLoading) return; // 防止重复点击
        
        try {
            setIsLoading(true);
            // 调用 getRecentModel 获取 credential（submit 和 getRecentModel 都会返回 credential）
            await axios.get(currentHostIP + '/api/getRecentModel', {withCredentials: true});
            
            navigate('/voicecall');
        } catch (error) {
            console.error('获取凭证失败:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 一步到位：提交表单并跳转到视频连线
    const handleSubmitAndGoToVoiceCall = async (e) => {
        if (e) {
            e.preventDefault();
        }

        // Reset error messages
        setErrorGender('');
        setErrorName('');
        setErrorIdCard('');
        setErrorProvince('');
        setErrorDistrict('');
        setErrorHospital('');
        setErrorTown('');
        setErrorVillage('');
        setErrorIdentity('');
        setErrorHpvUserRole('');
        setErrorHpvBirthDate('');
        setErrorHpvLocation('');
        setErrorHpvInitialAttitude('');

        // Reset cancer application error messages
        setErrorPatientName('');
        setErrorPatientGender('');
        setErrorPatientPhone('');
        setErrorRelationship('');
        setErrorPatientProvince('');
        setErrorPatientDistrict('');
        setErrorPatientHospital('');

        // Reset cancer application form fields
        setPatientProvinceCity('');

        let valid = true;

        if (isFluStyleSite) {
            setErrorFluRelationship('');
            setRsvError('');
            if (isRsvSite) {
                if (genderMale === null) {
                    setErrorGender('请选择婴儿的性别');
                    valid = false;
                }
                const birth = new Date(`${rsvBirthDate}T00:00:00`);
                if (!['母亲', '父亲', '医生', '其他'].includes(rsvRelationship)) {
                    setRsvError('请选择您与婴儿的关系');
                    valid = false;
                } else if (!rsvBirthDate || !Number.isFinite(birth.getTime()) || Number(rsvBirthDay) > rsvDaysInMonth) {
                    setRsvError('请选择今年有效的出生日期或预产期（月日）');
                    valid = false;
                }
            } else {
                if (!/^[0-9]{3}[0-9X]$/.test(idCardLastFour)) {
                    setErrorIdCard('请输入身份证后四位，前三位为数字，最后一位为数字或X');
                    valid = false;
                }
                if (!['本人', '配偶', '子女', '医生', '其他'].includes(fluRelationship)) {
                    setErrorFluRelationship('请选择您与患者的关系');
                    valid = false;
                }
                if (genderMale === null) {
                    setErrorGender('请选择性别');
                    valid = false;
                }
            }
            if (!selectedProvince) {
                setErrorProvince('请选择您所在的省市');
                valid = false;
            }
            if (!isRsvSite && !isJiaxing && districts.length > 0 && !selectedDistrict) {
                setErrorDistrict('请选择您所在的区县');
                valid = false;
            }
            if ((hasFluHospitals && !fluHospitals.includes(selectedHospital)) || (isRsvShanghai && !shanghaiRsvHospitals.includes(selectedHospital)) || (isJiaxing && !jiaxingHospitals.includes(selectedHospital)) || (!isRsvSite && !isJiaxing && districts.length > 0 && !selectedHospital)) {
                setErrorHospital('请选择您所在的社区医院');
                valid = false;
            }
        } else if (siteType === 'hpv') {
            if (!name.trim()) {
                setErrorName('请输入如何称呼您');
                valid = false;
            }

            if (!hpvUserRole) {
                setErrorHpvUserRole('请选择您和就诊者的关系');
                valid = false;
            }

            if (!hpvBirthDate) {
                setErrorHpvBirthDate('请选择出生日期');
                valid = false;
            }

            if (!hpvProvince || !hpvDistrict) {
                setErrorHpvLocation('请选择完整的所在地区');
                valid = false;
            }

            if (!hpvInitialAttitude) {
                setErrorHpvInitialAttitude('请选择目前最真实的感受或打算');
                valid = false;
            }
        } else if (siteType === 'cancer') {
            // cancer应用的验证逻辑
            if (!relationship) {
                setErrorRelationship('请选择您和筛查者的关系');
                valid = false;
            }

            if (!patientName.trim()) {
                setErrorPatientName('请输入筛查者的姓名');
                valid = false;
            }

            if (!patientGender) {
                setErrorPatientGender('请选择筛查者的性别');
                valid = false;
            }

            if (!patientPhoneLastFour) {
                setErrorPatientPhone('请输入筛查者手机号后4位');
                valid = false;
            } else if (patientPhoneLastFour.length !== 4) {
                setErrorPatientPhone('筛查者手机号后4位格式不正确');
                valid = false;
            } else if (!/^[0-9]{4}$/.test(patientPhoneLastFour)) {
                setErrorPatientPhone('筛查者手机号后4位格式不正确，只能输入数字');
                valid = false;
            }

            if (!patientProvince) {
                setErrorPatientProvince('请选择筛查者所在的省市');
                valid = false;
            }

            if (!patientDistrict) {
                setErrorPatientDistrict('请选择筛查者所在的区县');
                valid = false;
            }

            if (!patientHospital) {
                setErrorPatientHospital('请选择筛查者所在的医院');
                valid = false;
            }
        }

        if (!valid) return; // Stop submission if any field is invalid
        
        if (isLoading) return; // 防止重复提交
        setIsLoading(true);

        const formData = isFluStyleSite ? {
            vaccine_type: isRsvSite ? 'RSV' : 'FLU',
            ...(isRsvSite ? {relationship: rsvRelationship, gender_male: genderMale, birth_date: rsvBirthDate}
                : {relationship: fluRelationship, gender_male: genderMale, id_card_last_four: idCardLastFour}),
            province: selectedProvince,
            city: selectedCity,
            district: selectedDistrict,
            hospital: selectedHospital
        } : siteType === 'hpv' ? {
            name: name,
            user_role: hpvUserRole,
            birth_date: hpvBirthDate,
            user_location: `${hpvProvince}/${hpvDistrict}`,
            initial_attitude: hpvInitialAttitude
        } : {
            // cancer应用的数据（直接使用筛查者信息）
            relationship: relationship,
            name: patientName,
            gender_male: patientGender === "男" ? 1 : 0,
            phone: patientPhoneLastFour,
            province: patientProvince,
            city: patientCity,
            district: patientDistrict,
            hospital: patientHospital
        };

        axios.defaults.withCredentials = true;

        try {
            // Submit the form data to the server
            await axios.post(currentHostIP + '/api/submit', formData);
            console.log('表单提交成功，准备跳转到视频连线');
            
            // 设置最近模型（如果需要）
            const modelName = getModelNameForSiteType(siteType);
            await axios.post(currentHostIP + `/api/setRecentModel`, {
                model: modelName
            });

            if (siteType === 'hpv') {
                storeHpvAppointmentRegion(hpvProvince);
            }
            
            // 直接跳转到视频连线页面
            navigate('/voicecall');
        } catch (error) {
            console.error('提交表单时发生错误!', error);
            setIsLoading(false); // 出错时重置loading状态
        }
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();

        // Reset error messages
        setErrorGender('');
        setErrorName('');
        setErrorIdCard('');
        setErrorProvince('');
        setErrorDistrict('');
        setErrorHospital('');
        setErrorTown('');
        setErrorVillage('');
        setErrorIdentity('');
        setErrorHpvUserRole('');
        setErrorHpvBirthDate('');
        setErrorHpvLocation('');
        setErrorHpvInitialAttitude('');

        // Reset cancer application error messages
        setErrorPatientName('');
        setErrorPatientGender('');
        setErrorPatientPhone('');
        setErrorRelationship('');
        setErrorPatientProvince('');
        setErrorPatientDistrict('');
        setErrorPatientHospital('');

        // Reset cancer application form fields
        setPatientProvinceCity('');

        let valid = true;

        if (isFluStyleSite) {
            setErrorFluRelationship('');
            setRsvError('');
            if (isRsvSite) {
                if (genderMale === null) {
                    setErrorGender('请选择婴儿的性别');
                    valid = false;
                }
                const birth = new Date(`${rsvBirthDate}T00:00:00`);
                if (!['母亲', '父亲', '医生', '其他'].includes(rsvRelationship)) {
                    setRsvError('请选择您与婴儿的关系');
                    valid = false;
                } else if (!rsvBirthDate || !Number.isFinite(birth.getTime()) || Number(rsvBirthDay) > rsvDaysInMonth) {
                    setRsvError('请选择今年有效的出生日期或预产期（月日）');
                    valid = false;
                }
            } else {
                if (!/^[0-9]{3}[0-9X]$/.test(idCardLastFour)) {
                    setErrorIdCard('请输入身份证后四位，前三位为数字，最后一位为数字或X');
                    valid = false;
                }
                if (!['本人', '配偶', '子女', '医生', '其他'].includes(fluRelationship)) {
                    setErrorFluRelationship('请选择您与患者的关系');
                    valid = false;
                }
                if (genderMale === null) {
                    setErrorGender('请选择性别');
                    valid = false;
                }
            }
            if (!selectedProvince) {
                setErrorProvince('请选择您所在的省市');
                valid = false;
            }
            if (!isRsvSite && !isJiaxing && districts.length > 0 && !selectedDistrict) {
                setErrorDistrict('请选择您所在的区县');
                valid = false;
            }
            if ((hasFluHospitals && !fluHospitals.includes(selectedHospital)) || (isRsvShanghai && !shanghaiRsvHospitals.includes(selectedHospital)) || (isJiaxing && !jiaxingHospitals.includes(selectedHospital)) || (!isRsvSite && !isJiaxing && districts.length > 0 && !selectedHospital)) {
                setErrorHospital('请选择您所在的社区医院');
                valid = false;
            }
        } else if (siteType === 'hpv') {
            if (!name.trim()) {
                setErrorName('请输入如何称呼您');
                valid = false;
            }

            if (!hpvUserRole) {
                setErrorHpvUserRole('请选择您和就诊者的关系');
                valid = false;
            }

            if (!hpvBirthDate) {
                setErrorHpvBirthDate('请选择出生日期');
                valid = false;
            }

            if (!hpvProvince || !hpvDistrict) {
                setErrorHpvLocation('请选择完整的所在地区');
                valid = false;
            }

            if (!hpvInitialAttitude) {
                setErrorHpvInitialAttitude('请选择目前最真实的感受或打算');
                valid = false;
            }
        } else if (siteType === 'cancer') {
            // cancer应用的验证逻辑
            if (!relationship) {
                setErrorRelationship('请选择您和筛查者的关系');
                valid = false;
            }

            if (!patientName.trim()) {
                setErrorPatientName('请输入筛查者的姓名');
                valid = false;
            }

            if (!patientGender) {
                setErrorPatientGender('请选择筛查者的性别');
                valid = false;
            }

            if (!patientPhoneLastFour) {
                setErrorPatientPhone('请输入筛查者手机号后4位');
                valid = false;
            } else if (patientPhoneLastFour.length !== 4) {
                setErrorPatientPhone('筛查者手机号后4位格式不正确');
                valid = false;
            } else if (!/^[0-9]{4}$/.test(patientPhoneLastFour)) {
                setErrorPatientPhone('筛查者手机号后4位格式不正确，只能输入数字');
                valid = false;
            }

            if (!patientProvince) {
                setErrorPatientProvince('请选择筛查者所在的省市');
                valid = false;
            }

            if (!patientDistrict) {
                setErrorPatientDistrict('请选择筛查者所在的区县');
                valid = false;
            }

            if (!patientHospital) {
                setErrorPatientHospital('请选择筛查者所在的医院');
                valid = false;
            }
        }

        if (!valid) return; // Stop submission if any field is invalid
        
        if (isLoading) return; // 防止重复提交
        setIsLoading(true);

        const formData = isFluStyleSite ? {
            vaccine_type: isRsvSite ? 'RSV' : 'FLU',
            ...(isRsvSite ? {relationship: rsvRelationship, gender_male: genderMale, birth_date: rsvBirthDate}
                : {relationship: fluRelationship, gender_male: genderMale, id_card_last_four: idCardLastFour}),
            province: selectedProvince,
            city: selectedCity,
            district: selectedDistrict,
            hospital: selectedHospital
        } : siteType === 'hpv' ? {
            name: name,
            user_role: hpvUserRole,
            birth_date: hpvBirthDate,
            user_location: `${hpvProvince}/${hpvDistrict}`,
            initial_attitude: hpvInitialAttitude
        } : {
            // cancer应用的数据（直接使用筛查者信息）
            relationship: relationship,
            name: patientName,
            gender_male: patientGender === "男" ? 1 : 0,
            phone: patientPhoneLastFour,
            province: patientProvince,
            city: patientCity,
            district: patientDistrict,
            hospital: patientHospital
        };

        axios.defaults.withCredentials = true;

        // Submit the form data to the server
        axios.post(currentHostIP + '/api/submit', formData)
            .then(response => {
                console.log('表单提交成功:', response.data);
                const modelName = getModelNameForSiteType(siteType);
                return axios.post(currentHostIP + `/api/setRecentModel`, {
                    model: modelName
                }).then(response => {
                    const queryParams = new URLSearchParams({
                        name: formData.name || '',
                        mobile: isMobile ? 'true' : 'false'
                    });
                    if (isFluStyleSite) {
                        if (!isRsvSite) queryParams.set('gender', genderMale);
                    }
                    if (siteType === 'hpv') {
                        queryParams.set('initial_attitude', hpvInitialAttitude);
                    }
                    appendLocalApiHost(queryParams);
                    if (siteType === 'hpv') {
                        storeHpvAppointmentRegion(hpvProvince);
                        sessionStorage.setItem(HPV_EXIT_REMINDER_KEY, 'true');
                    }
                    navigate(`/chat?${queryParams.toString()}`);
                });
            })
            .catch(error => {
                console.error('提交表单时发生错误!', error);
                setIsLoading(false); // 出错时重置loading状态
            });
    };

    // 根据siteType类型配置不同的内容
    const siteConfig = {
        vax: {
            logo1: '/logo192.png',
            title1: '复旦大学',
            logo2: '/cdc.png',
            title2: '疾控中心',
            welcomeTitle: '流感疫苗，你问我答，快来咨询吧！',
            welcomeText: '感谢您使用流感疫苗智能问答助手，\n疾控中心和复旦大学通力合作，\n随时为您提供专业解答！',
            disclaimer: '流感疫苗智能问答助手提供的所有信息仅供参考\n 并不构成或替代医学专业建议。'
        },
        cancer: {
            logo1: '/logo192.png',
            title1: '复旦大学',
            logo2: '/cdc.png',
            title2: '疾控中心',
            welcomeTitle: '肠癌筛查，你问我答，快来咨询吧！',
            welcomeText: '感谢您使用肠癌筛查智能问答助手，\n疾控中心和复旦大学通力合作，\n随时为您提供专业解答！',
            disclaimer: '肠癌筛查智能问答助手提供的所有信息仅供参考\n 并不构成或替代医学专业建议。'
        },
        hpv: {
            logo1: '/logo192.png',
            title1: '复旦大学',
            logo2: '/cdc.png',
            title2: '疾控中心',
            welcomeTitle: 'HPV疫苗，你问我答，快来咨询吧！',
            welcomeText: '感谢您使用HPV疫苗智能问答助手，\n复旦大学和疾控中心通力合作，\n随时为您提供专业解答！',
            disclaimer: 'HPV 疫苗专属智能助手提供的所有信息仅供参考\n 并不构成或替代医学专业建议。'
        },
        rsv: {
            logo1: '/logo192.png',
            title1: '复旦大学',
            logo2: '/cdc.png',
            title2: '疾控中心',
            welcomeTitle: 'RSV预防，你问我答，快来咨询吧！',
            welcomeText: '感谢您使用RSV预防智能问答助手，\n复旦大学和疾控中心通力合作，\n随时为您提供专业解答！',
            disclaimer: 'RSV预防智能问答助手提供的所有信息仅供参考\n并不构成或替代医学专业建议。'
        }
    };
    
    const currentConfig = siteConfig[siteType] || siteConfig.vax;

    return (
        <div className={`${styles.body} ${siteType === 'hpv' ? styles.hpvBody : ''}`}>
            <div className={styles.header}>
                <div className={styles.header_row}>
                    <img src={currentConfig.logo1} alt="Logo" className={styles.logo}/>
                    <div className={styles.title}>{currentConfig.title1}</div>
                    <img src={currentConfig.logo2} alt="Logo" className={styles.logo}/>
                    <div className={styles.title}>{currentConfig.title2}</div>
                </div>
            </div>

            <h2>{welcomeMessage}</h2>
            {!showForm ? (
                <div>
                    <p className={styles.text}><strong>{currentConfig.welcomeTitle}</strong><br/>
                        {currentConfig.welcomeText}
                    </p>
                    {/* 自动朗读开关 */}
                    <div className={styles.formDiv}>
                        <label className={styles.label}>
                            <input
                                type="checkbox"
                                checked={autoPlay}
                                onChange={(e) => setAutoPlay(e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxLabel}>自动朗读回复（默认开启）</span>
                        </label>
                    </div>
                    <button 
                        onClick={() => handleContinueChat(false)} 
                        className={styles.button}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className={styles.spinner}></span>
                                加载中...
                            </>
                        ) : (
                            '继续之前的文字/语音咨询'
                        )}
                    </button>
                    <button 
                        onClick={() => handleContinueChat(true)} 
                        className={styles.button}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className={styles.spinner}></span>
                                加载中...
                            </>
                        ) : (
                            '重新开始新的文字/语音咨询'
                        )}
                    </button>
                    {siteType !== 'hpv' && (
                        <button
                            onClick={handleGoToVoiceCall}
                            className={styles.button}
                            disabled={isLoading}
                            style={{ backgroundColor: '#667eea', marginTop: '10px' }}
                        >
                            {isLoading ? (
                                <>
                                    <span className={styles.spinner}></span>
                                    加载中...
                                </>
                            ) : (
                                '📹 开始视频咨询'
                            )}
                        </button>
                    )}
                    {/*<button onClick={() => handleLeave()} className={styles.button}>前往接种登记</button>*/}
                </div>
            ) : (
                <form onSubmit={handleSubmit} className={`${styles.form} ${siteType === 'hpv' ? styles.hpvForm : ''}`}>
                    <p className={`${styles.text} ${siteType === 'hpv' ? styles.hpvIntro : ''}`}><strong>{currentConfig.welcomeTitle}</strong><br/>
                        {currentConfig.welcomeText}
                    </p>

                    {siteType === 'hpv' && (
                        <div className={styles.hpvQuestionnaire}>
                            <div className={styles.formDiv}>
                                <label className={styles.label}>
                                    如何称呼您：
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    maxLength="20"
                                    placeholder="请输入您的称呼"
                                    className={styles.inputText}
                                />
                                {errorName && <span className={styles.error}>{errorName}</span>}
                            </div>

                            <div className={styles.formDiv}>
                                <label className={styles.label}>
                                    您是就诊者的：
                                </label>
                                <div className={styles.hpvRadioGroup}>
                                    {[
                                        '本人',
                                        '父母',
                                        '对象',
                                        '朋友',
                                        '医生'
                                    ].map((role) => (
                                        <label key={role} className={styles.hpvRadioOption}>
                                            <input
                                                type="radio"
                                                name="user_role"
                                                value={role}
                                                checked={hpvUserRole === role}
                                                onChange={(e) => setHpvUserRole(e.target.value)}
                                                className={styles.inputRadio}
                                            />
                                            <span className={styles.inputRadioLabel}>{role}</span>
                                        </label>
                                    ))}
                                </div>
                                {errorHpvUserRole && <span className={styles.error}>{errorHpvUserRole}</span>}
                            </div>

                            <div className={styles.formDiv}>
                                <label className={styles.label}>
                                    就诊者出生日期：
                                </label>
                                <div className={styles.hpvDatePickerGroup}>
                                    <select
                                        value={hpvBirthYear}
                                        onChange={(e) => {
                                            setHpvBirthYear(e.target.value);
                                            setErrorHpvBirthDate('');
                                        }}
                                        className={styles.hpvDateSelect}
                                    >
                                        <option value="">年</option>
                                        {hpvBirthYears.map((year) => (
                                            <option key={year} value={year}>
                                                {year}年
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        value={hpvBirthMonth}
                                        onChange={(e) => {
                                            setHpvBirthMonth(e.target.value);
                                            setErrorHpvBirthDate('');
                                        }}
                                        className={styles.hpvDateSelect}
                                        disabled={!hpvBirthYear}
                                    >
                                        <option value="">月</option>
                                        {hpvBirthMonths.map((month) => (
                                            <option key={month} value={month}>
                                                {month}月
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        value={hpvBirthDay}
                                        onChange={(e) => {
                                            setHpvBirthDay(e.target.value);
                                            setErrorHpvBirthDate('');
                                        }}
                                        className={styles.hpvDateSelect}
                                        disabled={!hpvBirthYear || !hpvBirthMonth}
                                    >
                                        <option value="">日</option>
                                        {hpvBirthDays.map((day) => (
                                            <option key={day} value={day}>
                                                {day}日
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {errorHpvBirthDate && <span className={styles.error}>{errorHpvBirthDate}</span>}
                            </div>

                            <div className={styles.formDiv}>
                                <label className={styles.label}>
                                    所在省份：
                                </label>
                                <select
                                    value={hpvProvince}
                                    onChange={(e) => {
                                        setHpvProvince(e.target.value);
                                        setHpvDistrict('');
                                    }}
                                    className={styles.select}
                                >
                                    <option value="">选择省份</option>
                                    {hpvLocations.map((location) => (
                                        <option key={location.province} value={location.province}>
                                            {location.province}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {hpvProvince && (
                                <div className={styles.formDiv}>
                                    <label className={styles.label}>
                                        所在地区：
                                    </label>
                                    <select
                                        value={hpvDistrict}
                                        onChange={(e) => setHpvDistrict(e.target.value)}
                                        className={styles.select}
                                    >
                                        <option value="">选择地区</option>
                                        {hpvDistricts.map((district) => (
                                            <option key={district} value={district}>
                                                {district}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {errorHpvLocation && <span className={styles.error}>{errorHpvLocation}</span>}

                            <div className={styles.formDiv}>
                                <label className={styles.label}>
                                    关于HPV（宫颈癌）疫苗，您的看法是：
                                </label>
                                <div className={styles.hpvRadioGroup}>
                                    {[
                                        {
                                            value: 'A',
                                            text: '想打，希望了解接种流程'
                                        },
                                        {
                                            value: 'B',
                                            text: '还在纠结，想多了解一下再决定'
                                        },
                                        {
                                            value: 'C',
                                            text: '不想打，没必要接种'
                                        }
                                    ].map((attitude) => (
                                        <label key={attitude.value} className={styles.hpvRadioOption}>
                                            <input
                                                type="radio"
                                                name="initial_attitude"
                                                value={attitude.value}
                                                checked={hpvInitialAttitude === attitude.value}
                                                onChange={(e) => setHpvInitialAttitude(e.target.value)}
                                                className={styles.inputRadio}
                                            />
                                            <span className={styles.inputRadioLabel}>{attitude.value}. {attitude.text}</span>
                                        </label>
                                    ))}
                                </div>
                                {errorHpvInitialAttitude && <span className={styles.error}>{errorHpvInitialAttitude}</span>}
                            </div>
                        </div>
                    )}

                    {isRsvSite && (
                        <>
                            <div className={styles.formDiv}>
                                <label className={styles.label} htmlFor="rsv-login-relationship">您是婴儿的：</label>
                                <select id="rsv-login-relationship" className={styles.select} value={rsvRelationship} onChange={e => setRsvRelationship(e.target.value)}>
                                    <option value="">请选择</option>
                                    {['母亲', '父亲', '医生', '其他'].map(role => <option key={role} value={role}>{role}</option>)}
                                </select>
                            </div>
                            <div className={styles.formDiv}>
                                <label className={styles.label} htmlFor="rsv-infant-gender">您家婴儿的性别：</label>
                                <select id="rsv-infant-gender" className={styles.select} value={genderMale ?? ''}
                                    onChange={e => {setGenderMale(e.target.value === '' ? null : Number(e.target.value)); setErrorGender('');}}>
                                    <option value="">请选择</option>
                                    <option value="1">男</option>
                                    <option value="0">女</option>
                                    <option value="2">未知</option>
                                </select>
                                {errorGender && <span className={styles.error}>{errorGender}</span>}
                            </div>
                            <div className={styles.formDiv}>
                                <label className={styles.label}>您家婴儿的出生日期（预产期）：</label>
                                <div style={{display: 'flex', gap: '8px', width: '100%'}}>
                                    <select aria-label="婴儿出生月份" className={styles.select} value={rsvBirthMonth} onChange={e => {setRsvBirthMonth(e.target.value); setRsvBirthDay('');}}>
                                        <option value="">月</option>
                                        {Array.from({length: 12}, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}月</option>)}
                                    </select>
                                    <select aria-label="婴儿出生日期" className={styles.select} value={rsvBirthDay} onChange={e => setRsvBirthDay(e.target.value)}>
                                        <option value="">日</option>
                                        {Array.from({length: rsvDaysInMonth}, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}日</option>)}
                                    </select>
                                </div>
                            </div>
                            {rsvError && <span className={styles.error}>{rsvError}</span>}
                        </>
                    )}

                    {/* cancer应用显示关系选择 */}
                    {siteType === 'cancer' && (
                        <>
                            <div className={styles.formDiv}>
                                <label className={styles.label}>
                                    您和肠癌筛查者的关系:
                                </label>
                                <select
                                    value={relationship}
                                    onChange={(e) => setRelationship(e.target.value)}
                                    className={styles.select}
                                >
                                    <option value="">请选择关系</option>
                                    <option value="本人">本人</option>
                                    <option value="配偶">配偶</option>
                                    <option value="子女">子女</option>
                                    <option value="医生">医生</option>
                                    <option value="其他">其他</option>
                                </select>
                            </div>
                            {errorRelationship && <span className={styles.error}>{errorRelationship}</span>}

                            <div className={styles.formDiv}>
                                <label className={styles.label}>筛查者的姓名：</label>
                                <input
                                    type="text"
                                    value={patientName}
                                    onChange={(e) => setPatientName(e.target.value)}
                                    maxLength="20"
                                    placeholder="请输入筛查者的姓名"
                                    className={styles.inputText}
                                />
                            </div>
                            {errorPatientName && <span className={styles.error}>{errorPatientName}</span>}

                            <div className={styles.formDiv}>
                                <label className={styles.label}>
                                    筛查者的性别:
                                </label>
                                <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                                    <button
                                        type="button"
                                        onClick={() => setPatientGender("男")}
                                        className={patientGender === "男" ? styles.toggleButtonActive : styles.toggleButton}
                                        style={{
                                            flex: 1,
                                            padding: '8px 16px',
                                            border: patientGender === "男" ? '2px solid #4CAF50' : '2px solid #ddd',
                                            backgroundColor: patientGender === "男" ? '#4CAF50' : '#fff',
                                            color: patientGender === "男" ? '#fff' : '#333',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        男
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPatientGender("女")}
                                        className={patientGender === "女" ? styles.toggleButtonActive : styles.toggleButton}
                                        style={{
                                            flex: 1,
                                            padding: '8px 16px',
                                            border: patientGender === "女" ? '2px solid #4CAF50' : '2px solid #ddd',
                                            backgroundColor: patientGender === "女" ? '#4CAF50' : '#fff',
                                            color: patientGender === "女" ? '#fff' : '#333',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        女
                                    </button>
                                </div>
                            </div>
                            {errorPatientGender && <span className={styles.error}>{errorPatientGender}</span>}

                            <div className={styles.formDiv}>
                                <label className={styles.label}>筛查者手机号后4位：</label>
                                <input
                                    type="tel"
                                    value={patientPhoneLastFour}
                                    onChange={(e) => {
                                        // 只允许数字，限制4位
                                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                                        setPatientPhoneLastFour(value);
                                    }}
                                    maxLength="4"
                                    placeholder="请输入筛查者手机号后4位"
                                    className={styles.inputText}
                                />
                            </div>
                            {errorPatientPhone && <span className={styles.error}>{errorPatientPhone}</span>}

                            <div className={styles.formDiv}>
                                <label className={styles.label}>
                                    筛查者所在的省市：
                                </label>
                                <select
                                    value={patientProvinceCity}
                                    onChange={(e) => {
                                        const selectedLocation = locations.find(loc => `${loc.province} ${loc.city}` === e.target.value);
                                        setPatientProvince(selectedLocation ? selectedLocation.province : '');
                                        setPatientCity(selectedLocation ? selectedLocation.city : '');
                                        setPatientProvinceCity(e.target.value);
                                        setPatientDistrict('');
                                        setPatientHospital('');
                                    }}
                                    className={styles.select}
                                >
                                    <option value="">选择省市</option>
                                    {locations.map((location, index) => (
                                        <option key={index} value={`${location.province} ${location.city}`}>
                                            {location.province} {location.city}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {errorPatientProvince && <span className={styles.error}>{errorPatientProvince}</span>}

                            {/* 筛查者区县选择 */}
                            {patientProvince && (
                                <div className={styles.formDiv}>
                                    <label className={styles.label}>
                                        筛查者所在的区县：
                                    </label>
                                    <select
                                        value={patientDistrict}
                                        onChange={(e) => {
                                            setPatientDistrict(e.target.value);
                                            setPatientHospital(''); // 重置医院选择
                                        }}
                                        className={styles.select}
                                    >
                                        <option value="">选择区县</option>
                                        {patientDistricts.map((district, index) => (
                                            <option key={index} value={district.name}>
                                                {district.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {errorPatientDistrict && <span className={styles.error}>{errorPatientDistrict}</span>}

                            {/* 筛查者医院选择 */}
                            {patientDistrict && (
                                <div className={styles.formDiv}>
                                    <label className={styles.label}>
                                        筛查者所在的村/社区：
                                    </label>
                                    <select
                                        value={patientHospital}
                                        onChange={(e) => setPatientHospital(e.target.value)}
                                        className={styles.select}
                                    >
                                        <option value="">选择村/社区</option>
                                        {patientHospitals.map((hospital, index) => (
                                            <option key={index} value={hospital}>
                                                {hospital}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {errorPatientHospital && <span className={styles.error}>{errorPatientHospital}</span>}
                        </>
                    )}
                    
                    {siteType === 'vax' && (
                        <div className={styles.formDiv}>
                            <label htmlFor="flu-relationship" className={styles.label}>您是患者的：</label>
                            <select id="flu-relationship" value={fluRelationship} className={styles.select}
                                onChange={e => {setFluRelationship(e.target.value); setErrorFluRelationship('');}}>
                                <option value="">请选择</option>
                                {['本人', '配偶', '子女', '医生', '其他'].map(role => <option key={role} value={role}>{role}</option>)}
                            </select>
                            {errorFluRelationship && <span className={styles.error}>{errorFluRelationship}</span>}
                        </div>
                    )}

                    {/* 流感登录性别 */}
                    {siteType === 'vax' && (
                        <>
                            <div className={styles.formDiv}>
                                <label className={styles.label}>
                                    您的性别:
                                </label>
                                <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                                    <button
                                        type="button"
                                        onClick={() => setGenderMale(1)}
                                        className={genderMale === 1 ? styles.toggleButtonActive : styles.toggleButton}
                                        style={{
                                            flex: 1,
                                            padding: '8px 16px',
                                            border: genderMale === 1 ? '2px solid #4CAF50' : '2px solid #ddd',
                                            backgroundColor: genderMale === 1 ? '#4CAF50' : '#fff',
                                            color: genderMale === 1 ? '#fff' : '#333',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        男
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setGenderMale(0)}
                                        className={genderMale === 0 ? styles.toggleButtonActive : styles.toggleButton}
                                        style={{
                                            flex: 1,
                                            padding: '8px 16px',
                                            border: genderMale === 0 ? '2px solid #4CAF50' : '2px solid #ddd',
                                            backgroundColor: genderMale === 0 ? '#4CAF50' : '#fff',
                                            color: genderMale === 0 ? '#fff' : '#333',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        女
                                    </button>
                                </div>
                            </div>
                            {errorGender && <span className={styles.error}>{errorGender}</span>}
                        </>
                    )}

                    {/* 流感登录身份信息 */}
                    {siteType === 'vax' && (
                        <>
                            <div className={styles.formDiv}>
                                <label className={styles.label}>您的身份证后四位：</label>
                                <input
                                    type="text"
                                    value={idCardLastFour}
                                    onChange={(e) => {
                                        // 允许数字和X，转换为大写
                                        const value = e.target.value.replace(/[^0-9Xx]/g, '').toUpperCase().slice(0, 4);
                                        setIdCardLastFour(value);
                                    }}
                                    maxLength="4"
                                    placeholder="请输入身份证后四位（如123X）"
                                    className={styles.inputText}
                                />
                            </div>
                            {errorIdCard && <span className={styles.error}>{errorIdCard}</span>}
                        </>
                    )}

                    {/* vax应用的省市区县医院选择 */}
                    {isFluStyleSite && (
                        <>
                            <div className={styles.formDiv}>
                                <label className={styles.label}>
                                    选择您所在的省市：
                                </label>
                                <select
                                    value={selectedProvince ? `${selectedProvince}|${selectedCity}` : ''}
                                    onChange={(e) => {
                                        const selectedLocation = locations.find(
                                            loc => `${loc.province}|${loc.city}` === e.target.value
                                        );
                                        setSelectedProvince(selectedLocation ? selectedLocation.province : '');
                                        setSelectedCity(selectedLocation ? selectedLocation.city : '');
                                        setSelectedDistrict('');
                                        setSelectedHospital('');
                                    }}
                                    className={styles.select}
                                >
                                    <option value="">选择省市</option>
                                    {locations.map((location, index) => (
                                        <option key={index} value={`${location.province}|${location.city}`}>
                                            {location.label || `${location.province} ${location.city}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {errorProvince && <span className={styles.error}>{errorProvince}</span>}

                            {/* 区县选择 */}
                            {!isRsvSite && !isJiaxing && districts.length > 0 && selectedProvince && (
                                <div className={styles.formDiv}>
                                    <label className={styles.label}>
                                        选择您所在的区县：
                                    </label>
                                    <select
                                        value={selectedDistrict}
                                        onChange={(e) => {
                                            setSelectedDistrict(e.target.value);
                                            setSelectedHospital('');
                                        }}
                                        className={styles.select}
                                    >
                                        <option value="">选择区县</option>
                                        {districts.map((district, index) => (
                                            <option key={index} value={district.name}>
                                                {district.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {!isRsvSite && !isJiaxing && errorDistrict && <span className={styles.error}>{errorDistrict}</span>}

                            {/* 社区医院选择 */}
                            {(hasFluHospitals || isRsvShanghai || isJiaxing || (!isRsvSite && selectedDistrict)) && (
                                <div className={styles.formDiv}>
                                    <label className={styles.label}>
                                        选择您所在的社区医院：
                                    </label>
                                    <select
                                        value={selectedHospital}
                                        onChange={(e) => setSelectedHospital(e.target.value)}
                                        className={styles.select}
                                    >
                                        <option value="">选择社区医院</option>
                                        {(hasFluHospitals ? fluHospitals : isRsvShanghai ? shanghaiRsvHospitals : isJiaxing ? jiaxingHospitals : hospitals).map((hospital, index) => (
                                            <option key={index} value={hospital}>
                                                {hospital}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {errorHospital && <span className={styles.error}>{errorHospital}</span>}
                        </>
                    )}

                    {/* cancer应用只需要姓名，不需要其他字段 */}

                    {/* 自动朗读开关 */}
                    <div className={styles.formDiv}>
                        <label className={styles.label}>
                            <input
                                type="checkbox"
                                checked={autoPlay}
                                onChange={(e) => setAutoPlay(e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxLabel}>自动朗读回复（默认开启）</span>
                        </label>
                    </div>

                    <button 
                        type="submit" 
                        className={styles.button}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className={styles.spinner}></span>
                                提交中...
                            </>
                        ) : (
                            siteType === 'hpv' ? '开启专属咨询' : '开始文字/语音咨询'
                        )}
                    </button>
                    {/*<button */}
                    {/*    type="button"*/}
                    {/*    onClick={handleSubmitAndGoToVoiceCall} */}
                    {/*    className={styles.button}*/}
                    {/*    disabled={isLoading}*/}
                    {/*    style={{ backgroundColor: '#667eea', marginTop: '10px' }}*/}
                    {/*>*/}
                    {/*    {isLoading ? (*/}
                    {/*        <>*/}
                    {/*            <span className={styles.spinner}></span>*/}
                    {/*            提交中...*/}
                    {/*        </>*/}
                    {/*    ) : (*/}
                    {/*        '📹 开始视频咨询'*/}
                    {/*    )}*/}
                    {/*</button>*/}
                    <p className={styles.disclaimer}>
                       {currentConfig.disclaimer}
                    </p>
                </form>
            )}
            <div>
                {showReminder && (
                    <div className={styles.reminder_popup}>
                        <button
                            type="button"
                            className={styles.reminderCloseButton}
                            onClick={() => setShowReminder(false)}
                            aria-label="关闭退出提醒"
                            disabled={isLoading}
                        >
                            ×
                        </button>
                        <h2>退出提醒</h2>
                        {siteType !== 'hpv' && siteType !== 'vax' && (
                            <p>我们建议完成10次问答；您当前还差 {Math.max(0, 10 - chatCount)} 次问答。要继续完成吗？</p>
                        )}
                        {siteType === 'hpv' && chatCount < HPV_TARGET_CONVERSATIONS && (
                            <p className={styles.hpvConversationProgress}>
                                还需聊天 {getRemainingHpvConversations(chatCount)} 次，即可领取红包。
                            </p>
                        )}
                        {siteType === 'hpv' && chatCount >= HPV_TARGET_CONVERSATIONS && (
                            <p className={styles.hpvConversationComplete}>
                                已完成 {chatCount} 次问答，<button
                                    type="button"
                                    className={styles.hpvSubsidyTrigger}
                                    onClick={() => setShowHpvSubsidyModal(true)}
                                >领取红包</button>
                            </p>
                        )}
                        {siteType === 'hpv' && hpvAppointmentInfo && (
                            <div className={styles.appointmentPanel}>
                                <p className={styles.appointmentText}>您也可以直接进入{hpvAppointmentInfo.region}预约入口。</p>
                                <a
                                    className={`${styles.button} ${styles.appointmentButton}`}
                                    href={hpvAppointmentInfo.url}
                                >
                                    {hpvAppointmentInfo.label}
                                </a>
                            </div>
                        )}
                        {siteType === 'hpv' && (
                            <div className={styles.nearestClinicBlock}>
                                {isClinicLoading ? (
                                    <p className={styles.clinicMessage}>正在查询离您最近的接种点...</p>
                                ) : (
                                    <>
                                        <ClinicReminderPanel data={nearestClinicData}/>
                                        {!clinicLocationRequested && !nearestClinicData && (
                                            <button
                                                type="button"
                                                className={`${styles.button} ${styles.locationButton}`}
                                                onClick={handleViewNearestClinic}
                                            >
                                                点击查看附近接种点
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                        {siteType === 'vax' && <p>您可以登记流感疫苗接种意向，提交后仍需向医院确认接种安排。</p>}
                        {isRsvSite && (
                            <div className={styles.appointmentPanel}>
                                <p className={styles.appointmentText}>
                                    目前支持上海市、浙江省嘉兴市、浙江省湖州市和湖北省武汉市的RSV预防服务预约登记。
                                </p>
                            </div>
                        )}
                        <div className={styles.button_group}>
                            <button 
                                className={styles.button} 
                                onClick={handleContinue}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className={styles.spinner}></span>
                                        加载中...
                                    </>
                                ) : (
                                    '继续对话'
                                )}
                            </button>
                            <button 
                                className={styles.button} 
                                onClick={handleLeave}
                                disabled={isLoading}
                            >
                                {isFluStyleSite ? '前往预约登记' : '狠心离开'}
                            </button>
                        </div>
                    </div>
                )}
                {showHpvSubsidyModal && (
                    <HpvSubsidyModal onClose={() => setShowHpvSubsidyModal(false)}/>
                )}
            </div>
            <br/>
            <a href="https://beian.miit.gov.cn/">许可证编号为：沪ICP备2024096236号</a>
            <br/>
            {/* 辅助功能按钮组 */}
            <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                {/* 重新播放用户引导按钮 */}
            <button
                onClick={() => {
                    localStorage.removeItem('hasShownVoiceGuide');
                        alert('已重置用户引导，将在下次进入时重新显示语音功能引导！');
                        // 不刷新页面，让用户可以继续使用
                }}
                style={{
                    padding: '5px 10px',
                    fontSize: '12px',
                        backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                }}
                    title="重新播放用户引导"
            >
                    🎯 重新播放用户引导
                </button>

                {/* 重新填写信息按钮 */}
                <button
                    onClick={() => {
                        if (window.confirm('确定要重新填写信息吗？这将清除当前的所有填写内容。')) {
                            // 重置所有表单字段
                            if (isFluStyleSite || siteType === 'hpv') {
                                setName('');
                            }
                            setFluRelationship('');
                            setErrorFluRelationship('');
                            setRsvRelationship('');
                            setRsvBirthMonth('');
                            setRsvBirthDay('');
                            setRsvError('');
                            setIdCardLastFour('');
                            setGenderMale(null);
                            setSelectedProvince('');
                            setSelectedCity('');
                            setSelectedDistrict('');
                            setSelectedHospital('');
                            setSelectedTown('');
                            setSelectedVillage('');
                            setIdentity('');
                            setPhone('');
                            setHpvUserRole('');
                            setHpvBirthDate('');
                            setHpvBirthYear('');
                            setHpvBirthMonth('');
                            setHpvBirthDay('');
                            setHpvProvince('');
                            setHpvDistrict('');
                            setHpvInitialAttitude('');
                            sessionStorage.removeItem(HPV_APPOINTMENT_REGION_KEY);

                            // 重置cancer应用专用字段
                            setPatientName('');
                            setPatientGender('');
                            setPatientPhoneLastFour('');
                            setRelationship('');
                            setPatientProvince('');
                            setPatientCity('');
                            setPatientProvinceCity('');
                            setPatientDistrict('');
                            setPatientHospital('');

                            // 重置错误状态
                            setErrorGender('');
                            if (isFluStyleSite || siteType === 'hpv') {
                                setErrorName('');
                            }
                            setErrorIdCard('');
                            setErrorProvince('');
                            setErrorDistrict('');
                            setErrorHospital('');
                            setErrorTown('');
                            setErrorVillage('');
                            setErrorIdentity('');
                            setErrorHpvUserRole('');
                            setErrorHpvBirthDate('');
                            setErrorHpvLocation('');
                            setErrorHpvInitialAttitude('');

                            // 重置cancer应用专用错误状态
                            setErrorPatientName('');
                            setErrorPatientGender('');
                            setErrorPatientPhone('');
                            setErrorRelationship('');
                            setErrorPatientProvince('');
                            setErrorPatientDistrict('');
                            setErrorPatientHospital('');

                            // 显示表单
                            setShowForm(true);
                            setWelcomeMessage('');
                            setShowReminder(false);

                            alert('已重置表单，请重新填写信息。');
                        }
                    }}
                    style={{
                        padding: '5px 10px',
                        fontSize: '12px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                    title="重新填写信息"
                >
                    📝 重新填写信息
            </button>
            </div>
        </div>
    );
};

export default UserForm;
